/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  fetchBatchOptions,
  fetchPaymentMethodOptions,
  fetchFollowUpStatusOptions,
  fetchPaymentStatusOptions,
  fetchApplicantStatusOptions,
} from 'helpers';
import Sidebar from 'components/layout/Sidebar/Sidebar';
import {
  FilterSection, FilterField, FilterActions, countActiveFilters,
} from 'components/layout/Sidebar/Filter';
import {
  CustomDatepicker as Datepicker,
  Select2,
  Select,
  CreatableSelect,
} from 'components/common';
import testResultOptions from 'constants/testResultOptions';
import interviewers from 'constants/interviewers';

import * as styles from './Sidebar.styles';

const FTPSidebar = ({
  startDate,
  callDate,
  endDate,
  callEndDate,
  setDateRange,
  setCallRange,
  filterCondition,
  setFilterCondition,
  handleChange,
  handleSelect2Change,
  handleSubmit,
  handleReset,
  // handleDownloadCSV,
  // isCsvDownloading,
  alertSelect,
}) => {
  const [options, setOptions] = useState({
    batchOptions: [],
    paymentMethodOptions: [],
    paymentStatusOptions: [],
    followUpStatusOptions: [],
    applicantStatusOptions: [],
  });

  const getPaymentOptions = () => fetchPaymentMethodOptions().then((resp) => resp);

  const getPaymentStatusOptions = () => fetchPaymentStatusOptions().then((resp) => resp);

  const getBatchOptions = () => fetchBatchOptions(true).then((resp) => resp.map((d) => ({
    label: d.label,
    value: d.id,
  })));

  const getFollowUpStatusOptions = () => fetchFollowUpStatusOptions().then((resp) => resp);
  const getApplicantStatusOptions = () => fetchApplicantStatusOptions().then((resp) => resp);

  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  useEffect(async () => {
    const [
      paymentOptions,
      batchOptions,
      paymentStatusOptions,
      followUpStatusOptions,
      applicantStatusOptions,
    ] = await Promise.all([
      getPaymentOptions(),
      getBatchOptions(),
      getPaymentStatusOptions(),
      getFollowUpStatusOptions(),
      getApplicantStatusOptions(),
    ]);
    setOptions((prevState) => ({
      ...prevState,
      paymentOptions,
      batchOptions,
      paymentStatusOptions,
      followUpStatusOptions,
      applicantStatusOptions,
    }));
  }, []);

  return (
    <Sidebar
      search={{
        name: 'search',
        value: filterCondition.search,
        onChange: handleChange,
        onSubmit: onFormSubmit,
        placeholder: 'search by name / email',
      }}
      activeCount={countActiveFilters(filterCondition, startDate, callDate)}
    >
      <form onSubmit={onFormSubmit}>
        <div css={styles.filterConditionsContainerStyles}>
          <FilterSection title="Dates">
            <FilterField label="applied date">
              <Datepicker
                portalId="filter-panel-popover"
                containerStyles={styles.datepickerContainerStyles}
                startDate={startDate}
                endDate={endDate}
                setDateRange={setDateRange}
                placeholder="date range"
              />
            </FilterField>
            <FilterField label="call date">
              <Datepicker
                portalId="filter-panel-popover"
                containerStyles={styles.datepickerContainerStyles}
                startDate={callDate}
                endDate={callEndDate}
                setDateRange={setCallRange}
                placeholder="call date range"
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Program">
            <FilterField label="batch">
              <Select2
                portalMenu
                containerStyles={styles.select2ContainerStyles}
                defaultValue={{ label: 'batch', value: '' }}
                options={options.batchOptions}
                placeholder="batch"
                onChange={(e) => handleSelect2Change(e, 'lastBatch')}
                value={filterCondition.lastBatch}
              />
            </FilterField>
            <FilterField label="test result">
              <CreatableSelect
                portalMenu
                filterCondition={filterCondition}
                setFilterCondition={setFilterCondition}
                containerStyles={styles.select2ContainerStyles}
                options={testResultOptions}
                placeholder="test result"
                value={filterCondition.testResults}
                alertSelect={alertSelect.testResults}
                name="testResults"
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Payment">
            <FilterField label="payment type">
              <CreatableSelect
                portalMenu
                filterCondition={filterCondition}
                setFilterCondition={setFilterCondition}
                containerStyles={styles.select2ContainerStyles}
                options={options.paymentOptions}
                placeholder="payment type"
                value={filterCondition.paymentType}
                alertSelect={alertSelect.paymentType}
                name="paymentType"
              />
            </FilterField>
            <FilterField label="payment status">
              <Select
                containerStyles={styles.selectContainerStyles}
                name="paymentStatus"
                options={options.paymentStatusOptions}
                placeholder="Payment Status"
                value={filterCondition.paymentStatus}
                onChange={handleChange}
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Follow-up">
            <FilterField label="follow up status">
              <CreatableSelect
                portalMenu
                filterCondition={filterCondition}
                setFilterCondition={setFilterCondition}
                containerStyles={styles.select2ContainerStyles}
                options={options.followUpStatusOptions}
                placeholder="follow up status"
                value={filterCondition.followUpStatus}
                alertSelect={alertSelect.followUpStatus}
                name="followUpStatus"
              />
            </FilterField>
            <FilterField label="follow up by">
              <CreatableSelect
                portalMenu
                filterCondition={filterCondition}
                setFilterCondition={setFilterCondition}
                containerStyles={styles.select2ContainerStyles}
                options={interviewers}
                placeholder="follow up by"
                value={filterCondition.followUpBy}
                alertSelect={alertSelect.followUpBy}
                name="followUpBy"
              />
            </FilterField>
            <FilterField label="call attempt">
              <Select
                containerStyles={styles.selectContainerStyles}
                name="call"
                options={[
                  {
                    label: '1',
                    value: '1',
                  },
                  {
                    label: '2',
                    value: '2',
                  },
                  {
                    label: '3',
                    value: '3',
                  },
                ]}
                placeholder="call attempt"
                value={filterCondition.call}
                onChange={handleChange}
              />
            </FilterField>
          </FilterSection>
        </div>
        <FilterActions applyType="submit" onReset={handleReset} />
      </form>
    </Sidebar>
  );
};

FTPSidebar.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  callDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  callEndDate: PropTypes.instanceOf(Date),
  setDateRange: PropTypes.func.isRequired,
  setCallRange: PropTypes.func.isRequired,
  filterCondition: PropTypes.shape({
    lastBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
    paymentStatus: PropTypes.string,
    paymentType: PropTypes.arrayOf(PropTypes.shape({})),
    followUpStatus: PropTypes.arrayOf(PropTypes.shape({})),
    attendanceStatus: PropTypes.string,
    isRegistrationPaid: PropTypes.string,
    applicantStatuses: PropTypes.arrayOf(PropTypes.shape({})),
    followUpBy: PropTypes.arrayOf(PropTypes.shape({})),
    testResults: PropTypes.arrayOf(PropTypes.shape({})),
    call: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  setFilterCondition: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSelect2Change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  // handleDownloadCSV: PropTypes.func.isRequired,
  // isCsvDownloading: PropTypes.bool.isRequired,
  alertSelect: PropTypes.shape({
    paymentType: PropTypes.bool,
    followUpStatus: PropTypes.bool,
    testResults: PropTypes.bool,
    applicantStatuses: PropTypes.bool,
    followUpBy: PropTypes.bool,
  }).isRequired,
};

FTPSidebar.defaultProps = {
  startDate: null,
  endDate: null,
  callDate: null,
  callEndDate: null,
};

export default FTPSidebar;
